// src/types.ts
import { z } from "zod";
var PluginExecutionLayerSchema = z.enum([
  "prompt_driven",
  "tool_composed",
  "native",
  "sandbox_bundle",
  "browser_rpc"
]);
var PluginSkillDeclarationSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  description: z.string().optional(),
  intents: z.array(z.string()).optional(),
  skillMdPath: z.string().optional(),
  executionLayer: PluginExecutionLayerSchema.optional(),
  entryPoint: z.string().optional()
});
var PluginTaskMapEntrySchema = z.object({
  skillGraph: z.array(z.string().min(1)).optional(),
  skillIds: z.array(z.string().min(1)).optional(),
  policyProfile: z.string().nullable().optional(),
  allowDegraded: z.boolean().optional()
});
var PluginManifestSchema = z.object({
  id: z.string().min(1).regex(/^[a-z0-9][a-z0-9._-]*$/),
  name: z.string().min(1),
  version: z.string().regex(/^\d+\.\d+/),
  description: z.string(),
  type: z.enum(["plugin", "domain_pack"]).optional(),
  author: z.string().optional(),
  license: z.string().optional(),
  platformVersion: z.string().optional(),
  vertical: z.string().min(1).optional(),
  taskTypes: z.array(z.string().min(1)).optional(),
  docTypes: z.array(z.string().min(1)).optional(),
  skills: z.array(z.union([z.string().min(1), PluginSkillDeclarationSchema])).optional(),
  tools: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    entryPoint: z.string()
  })).optional(),
  mcpServers: z.array(z.object({
    id: z.string(),
    transport: z.enum(["stdio", "http", "sse"]),
    command: z.string().optional(),
    args: z.array(z.string()).optional(),
    url: z.string().optional(),
    lifecycle: z.enum(["eager", "lazy"])
  })).optional(),
  hooks: z.array(z.object({
    event: z.string(),
    handler: z.string(),
    priority: z.number().optional()
  })).optional(),
  policyProfile: z.string().nullable().optional(),
  requiredAdapters: z.array(z.string().min(1)).optional(),
  supportedSurfaces: z.array(z.string().min(1)).optional(),
  sharedCapabilities: z.array(z.string().min(1)).optional(),
  taskMap: z.record(z.string().min(1), PluginTaskMapEntrySchema).optional(),
  permissions: z.array(z.string()).optional(),
  tenantIds: z.union([z.array(z.string()), z.null()]).optional(),
  defaultEnabled: z.boolean().optional()
}).passthrough().superRefine((manifest, ctx) => {
  if (manifest.type !== "domain_pack") return;
  if (!manifest.vertical) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["vertical"],
      message: "Domain pack manifest must declare vertical"
    });
  }
  if (!manifest.taskTypes || manifest.taskTypes.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["taskTypes"],
      message: "Domain pack manifest must declare at least one task type"
    });
  }
  const hasPackSkills = Array.isArray(manifest.skills) && manifest.skills.length > 0;
  const hasTaskMap = manifest.taskMap && Object.keys(manifest.taskMap).length > 0;
  if (!hasPackSkills && !hasTaskMap) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["skills"],
      message: "Domain pack manifest must declare skills or taskMap"
    });
  }
  if (manifest.taskMap && manifest.taskTypes) {
    const declaredTaskTypes = new Set(manifest.taskTypes);
    for (const taskType of Object.keys(manifest.taskMap)) {
      if (!declaredTaskTypes.has(taskType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["taskMap", taskType],
          message: "taskMap keys must also be listed in taskTypes"
        });
      }
    }
  }
});

// src/client.ts
var AgentClient = class {
  config;
  ws = null;
  connectionState = "disconnected";
  reconnectAttempts = 0;
  reconnectTimer = null;
  heartbeatTimer = null;
  pendingRequests = /* @__PURE__ */ new Map();
  eventListeners = /* @__PURE__ */ new Map();
  messageCounter = 0;
  connectPromise = null;
  constructor(config) {
    this.config = {
      autoReconnect: true,
      maxReconnectAttempts: 10,
      reconnectDelayMs: 1e3,
      connectTimeoutMs: 1e4,
      heartbeatIntervalMs: 3e4,
      ...config
    };
  }
  // ─── Connection Management ────────────────────────────────────
  async connect() {
    if (this.connectionState === "connected") return;
    if (this.connectPromise) return this.connectPromise;
    this.setConnectionState("connecting");
    this.connectPromise = this._doConnect();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }
  _doConnect() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error(`Connection timeout after ${this.config.connectTimeoutMs}ms`));
      }, this.config.connectTimeoutMs);
      try {
        const wsUrl = this.config.url.replace(/^http/, "ws");
        const ws = new WebSocket(wsUrl, ["boox-agent-v1"]);
        this.ws = ws;
        let authResolved = false;
        const onAuthOk = (event) => {
          try {
            const msg = JSON.parse(typeof event.data === "string" ? event.data : "");
            if (msg.type === "auth_ok") {
              authResolved = true;
              clearTimeout(timeout);
              this.setConnectionState("connected");
              this.reconnectAttempts = 0;
              this.startHeartbeat();
              resolve();
            } else if (msg.type === "error" || ws.readyState !== WebSocket.OPEN) {
              clearTimeout(timeout);
              reject(new Error(msg.message ?? "Auth failed"));
            }
          } catch {
          }
        };
        ws.onopen = () => {
          ws.send(JSON.stringify({ type: "auth", token: this.config.token }));
        };
        ws.onmessage = (event) => {
          if (!authResolved) {
            onAuthOk(event);
          }
          this.handleMessage(typeof event.data === "string" ? event.data : "");
        };
        ws.onclose = () => {
          clearTimeout(timeout);
          this.stopHeartbeat();
          this.setConnectionState("disconnected");
          this.rejectAllPending(new Error("Connection closed"));
          if (!authResolved) {
            reject(new Error("Connection closed before authentication"));
          }
          if (this.config.autoReconnect) {
            this.scheduleReconnect();
          }
        };
        ws.onerror = () => {
        };
      } catch (err) {
        clearTimeout(timeout);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }
  disconnect() {
    this.config.autoReconnect = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1e3, "Client disconnect");
      this.ws = null;
    }
    this.setConnectionState("disconnected");
    this.rejectAllPending(new Error("Client disconnected"));
  }
  getConnectionState() {
    return this.connectionState;
  }
  /** Reset reconnect counter so the next connect() attempt starts fresh.
   *  Call this before a user-initiated connect to allow recovery after
   *  the background reconnect loop has been exhausted. */
  resetReconnect() {
    this.reconnectAttempts = 0;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
  // ─── Session Management ───────────────────────────────────────
  async createSession(opts) {
    const resp = await this.sendCommand({
      type: "create-session",
      tenantId: opts.tenantId,
      agentId: opts.agentId,
      title: opts.title
    });
    if (resp.type === "session-created") return resp.session;
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async resumeSession(sessionId) {
    const resp = await this.sendCommand({ type: "resume-session", sessionId });
    if (resp.type === "session-resumed") return { session: resp.session, history: resp.history };
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async endSession(sessionId) {
    await this.sendCommand({ type: "end-session", sessionId });
  }
  async listSessions(tenantId, opts) {
    const resp = await this.sendCommand({
      type: "list-sessions",
      tenantId,
      limit: opts?.limit,
      offset: opts?.offset
    });
    if (resp.type === "sessions-list") return { sessions: resp.sessions, total: resp.total };
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async getSession(sessionId) {
    const resp = await this.sendCommand({ type: "get-session", sessionId });
    if (resp.type === "session-info") return resp.session;
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  // ─── Messaging ────────────────────────────────────────────────
  /**
   * Send a message and receive streaming events via async iterator.
   *
   * Usage:
   *   for await (const event of client.sendMessage(sessionId, 'hello')) {
   *     if (event.type === 'text-delta') process.stdout.write(event.text);
   *   }
   */
  async *sendMessage(sessionId, content, attachments, context) {
    this.ensureConnected();
    const streamId = `stream-${++this.messageCounter}-${Date.now()}`;
    const eventQueue = [];
    let resolve = null;
    let done = false;
    const onEvent = (event, evtSessionId) => {
      if (evtSessionId !== sessionId) return;
      eventQueue.push(event);
      if (event.type === "done" || event.type === "error") done = true;
      resolve?.();
    };
    this.on("stream-event", onEvent);
    try {
      this.ws.send(JSON.stringify({
        type: "send-message",
        sessionId,
        content,
        attachments,
        context,
        _streamId: streamId
      }));
      while (!done) {
        if (eventQueue.length > 0) {
          const event = eventQueue.shift();
          yield event;
          if (event.type === "done" || event.type === "error") return;
        } else {
          await new Promise((r) => {
            resolve = r;
          });
          resolve = null;
        }
      }
      while (eventQueue.length > 0) {
        yield eventQueue.shift();
      }
    } finally {
      this.off("stream-event", onEvent);
    }
  }
  // ─── Agent Management ─────────────────────────────────────────
  async listAgents(tenantId) {
    const resp = await this.sendCommand({ type: "list-agents", tenantId });
    if (resp.type === "agents-list") return resp.agents;
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async switchAgent(sessionId, agentId) {
    await this.sendCommand({ type: "switch-agent", sessionId, agentId });
  }
  // ─── Undo/Redo ────────────────────────────────────────────────
  async undo(sessionId) {
    const resp = await this.sendCommand({ type: "undo", sessionId });
    if (resp.type === "undo-result") return { undone: resp.undone, restoredTo: resp.restoredTo };
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async redo(sessionId) {
    const resp = await this.sendCommand({ type: "redo", sessionId });
    if (resp.type === "redo-result") return { redone: resp.redone };
    throw new Error(`Unexpected response: ${resp.type}`);
  }
  async cancel(sessionId) {
    await this.sendCommand({ type: "cancel", sessionId });
  }
  // ─── Event Listeners ─────────────────────────────────────────
  on(event, handler) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event).add(handler);
  }
  off(event, handler) {
    this.eventListeners.get(event)?.delete(handler);
  }
  // ─── Internals ────────────────────────────────────────────────
  setConnectionState(state) {
    this.connectionState = state;
    this.emit("connection-state", state);
  }
  emit(event, ...args) {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(...args);
        } catch {
        }
      }
    }
  }
  ensureConnected() {
    if (this.connectionState !== "connected" || !this.ws) {
      throw new Error("Not connected to Agent Server. Call connect() first.");
    }
  }
  async sendCommand(command) {
    this.ensureConnected();
    const requestId = `req-${++this.messageCounter}-${Date.now()}`;
    const payload = JSON.stringify({ ...command, _requestId: requestId });
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Command '${command.type}' timed out after 30s`));
      }, 3e4);
      this.pendingRequests.set(requestId, { resolve, reject, timeout });
      this.ws.send(payload);
    });
  }
  handleMessage(raw) {
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    const requestId = data._requestId;
    if (requestId && this.pendingRequests.has(requestId)) {
      const pending = this.pendingRequests.get(requestId);
      this.pendingRequests.delete(requestId);
      clearTimeout(pending.timeout);
      if (data.type === "error") {
        pending.reject(new Error(data.message ?? "Unknown error"));
      } else {
        pending.resolve(data);
      }
      return;
    }
    const sessionId = data.sessionId ?? "";
    const type = data.type;
    if ([
      "text-delta",
      "tool-call-start",
      "tool-call-args-delta",
      "tool-call-end",
      "tool-result",
      "message-complete",
      "session-update",
      "error",
      "done",
      "ping"
    ].includes(type)) {
      this.emit("stream-event", data, sessionId);
    } else if (type === "pong") {
    } else {
      this.emit("server-response", data);
    }
  }
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.connectionState === "connected") {
        this.ws.send(JSON.stringify({ type: "ping" }));
      }
    }, this.config.heartbeatIntervalMs);
  }
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  scheduleReconnect() {
    if (this.reconnectTimer) return;
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.emit("error", new Error(`Max reconnect attempts (${this.config.maxReconnectAttempts}) exceeded`));
      return;
    }
    const delay = Math.min(
      this.config.reconnectDelayMs * Math.pow(2, this.reconnectAttempts),
      3e4
    );
    this.reconnectAttempts++;
    this.setConnectionState("reconnecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect().catch((err) => {
        this.emit("error", err instanceof Error ? err : new Error(String(err)));
      });
    }, delay);
  }
  rejectAllPending(error) {
    for (const [id, pending] of this.pendingRequests) {
      clearTimeout(pending.timeout);
      pending.reject(error);
      this.pendingRequests.delete(id);
    }
  }
};

export {
  PluginManifestSchema,
  AgentClient
};
//# sourceMappingURL=chunk-JB3VBGDQ.mjs.map